import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Mail, KeyRound, AlertCircle } from "lucide-react";

/**
 * Customer login page. Reads ?token=... from the URL and POSTs to
 * /api/customer/auth/redeem. If no token, shows a "Please contact A1 to send
 * you a portal link" message — magic-link request flow is admin-driven in
 * dry-run mode and not customer-self-serve until live email is wired.
 */
export default function CustomerLogin() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState("idle"); // idle | redeeming | error | success
    const [error, setError] = useState("");

    useEffect(() => {
        const token = params.get("token");
        if (!token) return;
        setStatus("redeeming");
        api.post("/customer/auth/redeem", { token })
            .then(() => {
                setStatus("success");
                setTimeout(() => navigate("/customer/dashboard"), 200);
            })
            .catch((e) => {
                setStatus("error");
                setError(e?.response?.data?.detail || "Could not redeem this link.");
            });
    }, [params, navigate]);

    return (
        <div className="min-h-screen a1-bg-base flex items-center justify-center px-5 py-10">
            <div className="w-full max-w-md a1-card a1-card-feature flex flex-col gap-5" data-testid="customer-login">
                <div className="flex items-center gap-3">
                    <span className="a1-icon-wrap-lg"><KeyRound size={24} /></span>
                    <div>
                        <h2 className="text-2xl mb-1">Customer portal</h2>
                        <p className="text-sm uppercase tracking-wider font-bold" style={{ color: "var(--color-accent)" }}>
                            A1 Screening Services · Naples, FL
                        </p>
                    </div>
                </div>
                {status === "redeeming" ? (
                    <p className="text-sm" style={{ color: "var(--color-text-muted)" }}>Verifying your link…</p>
                ) : null}
                {status === "success" ? (
                    <p className="text-sm" style={{ color: "var(--color-success)" }}>Signed in. Redirecting…</p>
                ) : null}
                {status === "error" ? (
                    <div className="flex items-start gap-2" style={{ color: "var(--color-danger)" }} data-testid="customer-login-error">
                        <AlertCircle size={18} className="shrink-0 mt-0.5" />
                        <p className="text-sm">{error}</p>
                    </div>
                ) : null}
                {status === "idle" ? (
                    <div className="text-sm leading-relaxed" style={{ color: "var(--color-text-muted)" }}>
                        <p className="mb-4">
                            <strong style={{ color: "var(--color-text)" }}>Looking for your portal?</strong> Your A1 Screening account is opened by a secure one-time link we send by email or text after we prepare your estimate or invoice. To request a fresh link, call us at
                            <a href="tel:+12394347306" className="mx-1 font-bold">239-434-7306</a>
                            or email
                            <a href="mailto:info@a1screening247.com" className="mx-1">
                                <Mail size={12} className="inline mr-0.5" />info@a1screening247.com
                            </a>
                            and we'll send you a portal link.
                        </p>
                        <p>
                            If you arrived here from a portal-link email or text, please make sure you've opened the full URL — it should include a <code>?token=…</code> at the end.
                        </p>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
